// src/oura/onboarding/guides.ts
//
// The steps a client follows to give us access, per platform. One place, so
// the email we send and the page the merchant reads never say 2 different
// things.
//
// A step is a plain sentence. Words between asterisks are the names of things
// on the other product's screen and render in bold.
//
// Every access here is an invite or Google's own approval screen. The client
// never handles a key or a password: connecting Oura to a site for publishing
// is our job, done on the Connectors screen with our own user.

import { SITE_ROLE, type AccessKey, type SiteBuilder } from "@service/aeo/onboarding";

/** Every invite goes to this one address. */
export const ACCESS_EMAIL = "oura@ourteam.ai";

export interface SiteGuide {
    key: SiteBuilder;
    label: string;
    /** The name on the switcher, where 4 have to fit a phone. */
    short: string;
    /** Whether the platform sends us an invite, or the client adds a user for us. */
    kind: "invite" | "user";
    /** The role we ask for, in the platform's own word. */
    role: string;
    steps: string[];
    /** One line for the access request email. */
    requestLine: string;
    /** What the merchant presses once they have done it. */
    done: string;
}

export const SITE_GUIDES: Record<SiteBuilder, SiteGuide> = {
    wix: {
        key: "wix",
        label: "Wix",
        short: "Wix",
        kind: "invite",
        role: SITE_ROLE.wix,
        steps: [
            "Open your *Wix dashboard*, then *Settings*, then *Roles and permissions*.",
            `Choose *Invite people* and enter ${ACCESS_EMAIL}.`,
            "Pick the role *Website Manager*, and send the invite.",
        ],
        requestLine: `Wix. Settings, Roles and permissions, Invite people. Enter ${ACCESS_EMAIL} as Website Manager.`,
        done: "I've sent the invite",
    },
    wordpress: {
        key: "wordpress",
        label: "WordPress",
        short: "WordPress",
        kind: "user",
        role: SITE_ROLE.wordpress,
        steps: [
            "In WordPress, open *Users*, then *Add new user*.",
            `Username *oura*, email ${ACCESS_EMAIL}.`,
            "Pick the role *Administrator*. Site fixes need it.",
            "Press *Add new user*. WordPress emails us the rest.",
        ],
        requestLine: `WordPress. Users, Add new user. Enter ${ACCESS_EMAIL} as Administrator.`,
        done: "I've added the user",
    },
    framer: {
        key: "framer",
        label: "Framer",
        short: "Framer",
        kind: "invite",
        role: SITE_ROLE.framer,
        steps: [
            "In Framer, open your project and choose *Invite*.",
            `Enter ${ACCESS_EMAIL} as an *editor*.`,
            "Send the invite.",
        ],
        requestLine: `Framer. Open the project, Invite. Enter ${ACCESS_EMAIL} as an editor.`,
        done: "I've sent the invite",
    },
    other: {
        key: "other",
        label: "Another platform",
        short: "Other",
        kind: "user",
        role: SITE_ROLE.other,
        steps: [
            `In your website's admin area, add a *new user* for ${ACCESS_EMAIL}.`,
            "Give it the lowest role that can edit pages.",
            "Tell us here once it is done.",
        ],
        requestLine: `Your website. Add a new user for ${ACCESS_EMAIL} that can edit pages.`,
        done: "I've added the user",
    },
};
export const SITE_BUILDER_ORDER: SiteBuilder[] = ["wix", "wordpress", "framer", "other"];

export const GBP_STEPS: string[] = [
    "Open your *Business Profile* on Google, signed in as the owner.",
    "Open the menu, then *Business Profile settings*, then *People and access*.",
    `Choose *Add*, enter ${ACCESS_EMAIL}, and pick *Manager*.`,
];
export const GBP_REQUEST_LINE = `Google Business Profile. Settings, People and access, Add. Enter ${ACCESS_EMAIL} as Manager.`;

export const GOOGLE_STEPS: string[] = [
    "Press *Connect Google*.",
    "Choose the Google account that owns it.",
    "Approve *read only* access on Google's screen.",
];

/** "Or add us as a user" for Search Console and Analytics, read only (#4513). Shown only when the backend lists the claim. */
export const ADD_USER_STEPS: Record<"gsc" | "ga4", string[]> = {
    gsc: [
        "Open *Search Console* on Google, signed in as the owner.",
        "Choose *Settings*, then *Users and permissions*.",
        `Choose *Add user*, enter ${ACCESS_EMAIL}, and pick *Restricted*.`,
    ],
    ga4: [
        "Open *Google Analytics*, signed in as an admin.",
        "Choose *Admin*, then *Property access management*.",
        `Choose *Add users*, enter ${ACCESS_EMAIL}, and pick *Viewer*.`,
    ],
};

/** A claimed Search Console or Analytics row, until the live connection turns it connected (#4513). */
export const GOOGLE_WAITING = "We're checking. This turns green once it is connected.";

/** What each access is for, in the merchant's words. */
export const ACCESS_WHY: Record<AccessKey, string> = {
    gbp: "So we can keep your hours, services and photos right on Google.",
    website: "So we can put approved fixes live on your site.",
    gsc: "So we can see what people search before they find you.",
    ga4: "So we can see how many people visit, and from where.",
};

/** How each access is removed, so the merchant knows before they give it. */
export const ACCESS_REMOVE: Record<AccessKey, string> = {
    gbp: "Remove us under People and access, any time.",
    website: "Remove our user, any time.",
    gsc: "Disconnect here, any time.",
    ga4: "Disconnect here, any time.",
};

/** What stops when an access is removed. Said before the merchant confirms. */
export const ACCESS_LOSS: Record<AccessKey, string> = {
    gbp: "We stop updating your Google profile.",
    website: "We stop putting fixes live on your site.",
    gsc: "We stop seeing what people search.",
    ga4: "We stop seeing your visitor numbers.",
};

/** The names the merchant reads. The same on the list and in the drawer. */
export const MERCHANT_ACCESS_NAME: Record<AccessKey, string> = {
    gbp: "Google Business Profile",
    website: "Your website",
    gsc: "Google Search Console",
    ga4: "Google Analytics",
};
